import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const LondonVIPAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [notes, setNotes] = useState('');
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings'); // bookings, drivers, vehicles
  
  // Driver & Vehicle Management
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showEditVehicleModal, setShowEditVehicleModal] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', phone: '', email: '', photo_url: '', license_number: '' });
  const [newVehicle, setNewVehicle] = useState({ make: '', model: '', color: '', plate: '', year: '', vehicle_image_url: '' });
  const [editVehicle, setEditVehicle] = useState({ make: '', model: '', color: '', plate: '', year: '', vehicle_image_url: '' });
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [vehicleImageFile, setVehicleImageFile] = useState(null);
  const [vehicleImagePreview, setVehicleImagePreview] = useState('');
  const [uploadingVehicleImage, setUploadingVehicleImage] = useState(false);
  const [editVehicleImageFile, setEditVehicleImageFile] = useState(null);
  const [editVehicleImagePreview, setEditVehicleImagePreview] = useState('');
  const [uploadingEditVehicleImage, setUploadingEditVehicleImage] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  useEffect(() => {
    // Force scroll to top immediately and after render
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    scrollToTop();
    setTimeout(scrollToTop, 0);
    requestAnimationFrame(scrollToTop);
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if user is admin
      const { data: adminData } = await supabase
        .from('admins')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!adminData) {
        alert('Access denied. Admin access required.');
        navigate('/');
        return;
      }

      fetchBookings();
      fetchDrivers();
      fetchVehicles();
    } catch (error) {
      console.error('Error checking auth:', error);
      navigate('/login');
    }
  };

  const fetchBookings = async () => {
    try {
      let query = supabase
        .from('vip_protect_bookings')
        .select('*')
        .order('service_date', { ascending: true })
        .order('service_time', { ascending: true });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchBookings();
    }
  }, [filter]);

  // Scroll to top when changing tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', bookingId);

      if (error) throw error;

      fetchBookings();
      alert(`Booking ${newStatus} successfully`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating booking status');
    }
  };

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('is_active', true)
        .order('make');

      if (error) throw error;
      setVehicles(data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const addDriver = async () => {
    try {
      const { error } = await supabase
        .from('drivers')
        .insert([newDriver]);

      if (error) throw error;

      fetchDrivers();
      setShowAddDriverModal(false);
      setNewDriver({ name: '', phone: '', email: '', photo_url: '', license_number: '' });
      alert('Driver added successfully!');
    } catch (error) {
      console.error('Error adding driver:', error);
      alert('Error adding driver');
    }
  };

  const uploadVehicleImage = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
    const filePath = `vehicle-images/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('content-media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw new Error(`Vehicle image upload failed: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('content-media')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const uploadTransactionDocument = async (file, bookingId, documentType) => {
    const safeType = documentType === 'invoice' ? 'invoice' : 'receipt';
    const fileExt = file.name.split('.').pop();
    const fileName = `${safeType}-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `booking-documents/${bookingId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('content-media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      throw new Error(`${safeType} upload failed: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('content-media')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const saveTransactionDocument = async (documentType, file) => {
    if (!selectedBooking || !file) return;

    const isInvoice = documentType === 'invoice';
    isInvoice ? setUploadingInvoice(true) : setUploadingReceipt(true);

    try {
      const publicUrl = await uploadTransactionDocument(file, selectedBooking.id, documentType);
      const updatePayload = {
        updated_at: new Date().toISOString(),
        ...(isInvoice
          ? { invoice_url: publicUrl, invoice_uploaded_at: new Date().toISOString() }
          : { receipt_url: publicUrl, receipt_uploaded_at: new Date().toISOString() })
      };

      const { data, error } = await supabase
        .from('vip_protect_bookings')
        .update(updatePayload)
        .eq('id', selectedBooking.id)
        .select('*')
        .single();

      if (error) throw error;

      setSelectedBooking(data || { ...selectedBooking, ...updatePayload });
      setBookings((prev) => prev.map((booking) => (
        booking.id === selectedBooking.id ? { ...booking, ...(data || updatePayload) } : booking
      )));
      alert(`${isInvoice ? 'Invoice' : 'Receipt'} uploaded successfully`);
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error);
      if ((error.message || '').toLowerCase().includes('column')) {
        alert('Upload failed because invoice/receipt columns are missing in the database. Run the new SQL migration first.');
      } else {
        alert(`Error uploading ${documentType}: ${error.message || 'Unknown error'}`);
      }
    } finally {
      isInvoice ? setUploadingInvoice(false) : setUploadingReceipt(false);
    }
  };

  const handleVehicleImageSelect = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    setVehicleImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setVehicleImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const addVehicle = async () => {
    try {
      if (!vehicleImageFile) {
        alert('Please upload a vehicle image');
        return;
      }

      const vehicleYear = newVehicle.year ? parseInt(newVehicle.year, 10) : null;
      if (vehicleYear !== null && Number.isNaN(vehicleYear)) {
        alert('Year must be a valid number');
        return;
      }

      setUploadingVehicleImage(true);
      const vehicleImageUrl = await uploadVehicleImage(vehicleImageFile);

      const vehiclePayload = {
        make: newVehicle.make.trim(),
        model: newVehicle.model.trim(),
        color: newVehicle.color.trim(),
        plate: newVehicle.plate.trim().toUpperCase(),
        year: vehicleYear,
        vehicle_image_url: vehicleImageUrl
      };

      const { error} = await supabase
        .from('vehicles')
        .insert([vehiclePayload]);

      if (error) throw error;

      fetchVehicles();
      setShowAddVehicleModal(false);
      setNewVehicle({ make: '', model: '', color: '', plate: '', year: '', vehicle_image_url: '' });
      setVehicleImageFile(null);
      setVehicleImagePreview('');
      alert('Vehicle added successfully!');
    } catch (error) {
      console.error('Error adding vehicle:', error);
      alert(`Error adding vehicle: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingVehicleImage(false);
    }
  };

  const openEditVehicleModal = (vehicle) => {
    setEditingVehicleId(vehicle.id);
    setEditVehicle({
      make: vehicle.make || '',
      model: vehicle.model || '',
      color: vehicle.color || '',
      plate: vehicle.plate || '',
      year: vehicle.year || '',
      vehicle_image_url: vehicle.vehicle_image_url || ''
    });
    setEditVehicleImageFile(null);
    setEditVehicleImagePreview(vehicle.vehicle_image_url || '');
    setShowEditVehicleModal(true);
  };

  const closeEditVehicleModal = () => {
    setShowEditVehicleModal(false);
    setEditingVehicleId(null);
    setEditVehicle({ make: '', model: '', color: '', plate: '', year: '', vehicle_image_url: '' });
    setEditVehicleImageFile(null);
    setEditVehicleImagePreview('');
  };

  const handleEditVehicleImageSelect = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    setEditVehicleImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setEditVehicleImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const updateVehicle = async () => {
    try {
      if (!editingVehicleId) return;

      const vehicleYear = editVehicle.year ? parseInt(editVehicle.year, 10) : null;
      if (vehicleYear !== null && Number.isNaN(vehicleYear)) {
        alert('Year must be a valid number');
        return;
      }

      let vehicleImageUrl = editVehicle.vehicle_image_url || null;
      if (editVehicleImageFile) {
        setUploadingEditVehicleImage(true);
        vehicleImageUrl = await uploadVehicleImage(editVehicleImageFile);
      }

      const vehiclePayload = {
        make: editVehicle.make.trim(),
        model: editVehicle.model.trim(),
        color: editVehicle.color.trim(),
        plate: editVehicle.plate.trim().toUpperCase(),
        year: vehicleYear,
        vehicle_image_url: vehicleImageUrl,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('vehicles')
        .update(vehiclePayload)
        .eq('id', editingVehicleId);

      if (error) throw error;

      fetchVehicles();
      closeEditVehicleModal();
      alert('Vehicle updated successfully!');
    } catch (error) {
      console.error('Error updating vehicle:', error);
      alert(`Error updating vehicle: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingEditVehicleImage(false);
    }
  };

  const assignDriver = async (bookingId) => {
    try {
      const driver = drivers.find(d => d.id === selectedDriverId);
      const vehicle = vehicles.find(v => v.id === selectedVehicleId);

      if (!driver || !vehicle) {
        alert('Please select both driver and vehicle');
        return;
      }

      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ 
          driver_id: driver.id,
          vehicle_id: vehicle.id,
          driver_name: driver.name,
          driver_phone: driver.phone,
          driver_photo_url: driver.photo_url,
          vehicle_make: vehicle.make,
          vehicle_model: vehicle.model,
          vehicle_color: vehicle.color,
          vehicle_plate: vehicle.plate,
          driver_assigned_at: new Date().toISOString(),
          status: 'confirmed',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (error) throw error;

      fetchBookings();
      setShowDriverModal(false);
      setSelectedDriverId('');
      setSelectedVehicleId('');
      alert('Driver and vehicle assigned successfully!');
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Error assigning driver');
    }
  };

  const openDriverModal = (booking) => {
    setSelectedBooking(booking);
    setSelectedDriverId(booking.driver_id || '');
    // Pre-fill with assigned vehicle if already set, otherwise fall back to what the client requested
    setSelectedVehicleId(booking.vehicle_id || booking.requested_vehicle_id || '');
    setShowDriverModal(true);
  };

  const updateNotes = async (bookingId) => {
    try {
      const { error } = await supabase
        .from('vip_protect_bookings')
        .update({ notes: notes, updated_at: new Date().toISOString() })
        .eq('id', selectedBooking.id);

      if (error) throw error;

      fetchBookings();
      setSelectedBooking(null);
      setNotes('');
      alert('Notes updated successfully');
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Error updating notes');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#CDB688';
      case 'confirmed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'completed': return '#808080';
      case 'cancelled': return '#f44336';
      default: return '#000';
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: 'white',
        background: getStatusColor(status),
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;

  // Enhanced filtering
  const filteredBookings = bookings.filter(booking => {
    // Status filter
    const statusMatch = filter === 'all' || booking.status === filter;
    
    // Search filter (name, email, phone, addresses)
    const searchMatch = !searchTerm || 
      booking.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.pickup_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.destination_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vehicle_make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Date filter
    const dateMatch = !dateFilter || booking.service_date === dateFilter;
    
    return statusMatch && searchMatch && dateMatch;
  });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const inProgressCount = bookings.filter(b => b.status === 'in_progress').length;

  return (
    <div className="page-container">
      <div className="page-wrapper" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
              London VIP Protect Admin
            </h1>
            <p style={{ color: '#666', fontSize: '0.95rem' }}>Chauffeur & Security Services Dashboard</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                border: '1px solid var(--gold)',
                color: 'var(--gold)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Back to Dashboard
            </button>
            <button
              onClick={handleSignOut}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f44336',
                border: 'none',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '2rem',
          borderBottom: '2px solid #f0f0f0',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('bookings')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'bookings' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'bookings' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('drivers')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'drivers' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'drivers' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Drivers ({drivers.length})
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'vehicles' ? '3px solid var(--gold)' : '3px solid transparent',
              color: activeTab === 'vehicles' ? 'var(--gold)' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Vehicles ({vehicles.length})
          </button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
        <>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#CDB688', marginBottom: '0.5rem' }}>{pendingCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Pending Requests</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', marginBottom: '0.5rem' }}>{confirmedCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Confirmed</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3', marginBottom: '0.5rem' }}>{inProgressCount}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>In Progress</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.5rem' }}>{bookings.length}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Bookings</div>
          </div>
        </div>

        {/* Filters */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          {/* Search and Date Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Search by name, email, phone, address, driver, vehicle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.95rem'
                }}
              />
            </div>
            {(searchTerm || dateFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                }}
                style={{
                  padding: '0.75rem 1rem',
                  background: '#f44336',
                  border: 'none',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Status Filters */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  padding: '0.5rem 1rem',
                  background: filter === status ? 'var(--gold)' : 'transparent',
                  border: `1px solid ${filter === status ? 'var(--gold)' : '#ccc'}`,
                  color: filter === status ? '#000' : '#666',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontWeight: filter === status ? 'bold' : 'normal'
                }}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Results count */}
          <div style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
            Showing {filteredBookings.length} of {bookings.length} booking(s)
          </div>
        </div>

        {/* Bookings List */}
        <div className="card" style={{ overflow: 'hidden' }}>
          {filteredBookings.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <div>No bookings found</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(205, 182, 136, 0.1)', borderBottom: '2px solid var(--gold)' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Date & Time</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Client</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Route</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Driver & Vehicle</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#000', fontWeight: 'bold' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <React.Fragment key={booking.id}>
                      <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          <div style={{ fontWeight: 'bold' }}>{formatDate(booking.service_date)}</div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            {formatTime(booking.service_time)} - {formatTime(booking.end_time)}
                          </div>
                          {booking.duration_hours && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: 'var(--gold)', fontWeight: 'bold' }}>
                              ⏱ {booking.duration_hours} hour(s)
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          <div style={{ fontWeight: 'bold' }}>{booking.user_name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>{booking.user_email}</div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>{booking.user_phone}</div>
                        </td>
                        <td style={{ padding: '1rem', color: '#000', maxWidth: '250px' }}>
                          <div style={{ marginBottom: '0.25rem' }}>
                            <strong style={{ fontSize: '0.85rem', color: '#666' }}>From:</strong>
                            <div style={{ fontSize: '0.9rem' }}>{booking.pickup_address}</div>
                          </div>
                          <div>
                            <strong style={{ fontSize: '0.85rem', color: '#666' }}>To:</strong>
                            <div style={{ fontSize: '0.9rem' }}>{booking.destination_address}</div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#000' }}>
                          {booking.driver_name ? (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {booking.driver_photo_url && (
                                  <img 
                                    src={booking.driver_photo_url} 
                                    alt={booking.driver_name}
                                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                )}
                                <div>
                                  <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{booking.driver_name}</div>
                                  <div style={{ fontSize: '0.8rem', color: '#666' }}>{booking.driver_phone}</div>
                                </div>
                              </div>
                              {booking.vehicle_make && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                  {booking.vehicle_color} {booking.vehicle_make} {booking.vehicle_model}
                                  <div style={{ fontWeight: 'bold', color: '#000' }}>{booking.vehicle_plate}</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>Not assigned</span>
                          )}
                          {/* Client requests */}
                          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {booking.requested_vehicle_id && vehicles.find(v => v.id === booking.requested_vehicle_id) && (() => {
                              const rv = vehicles.find(v => v.id === booking.requested_vehicle_id);
                              return (
                                <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', background: 'rgba(205,182,136,0.15)', border: '1px solid var(--gold)', borderRadius: '4px', color: '#000' }}>
                                  🚗 Req: {rv.color} {rv.make} {rv.model}
                                </span>
                              );
                            })()}
                            {booking.cpo_requested && (
                              <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', background: 'rgba(0,0,0,0.06)', border: '1px solid #999', borderRadius: '4px', color: '#000' }}>
                                🛡 CPO Requested
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {getStatusBadge(booking.status)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {booking.status === 'pending' && (
                              <button
                                onClick={() => openDriverModal(booking)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'var(--gold)',
                                  border: '2px solid var(--gold)',
                                  color: 'black',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                Assign Driver
                              </button>
                            )}
                            {(booking.status === 'confirmed' || booking.status === 'in_progress' || booking.status === 'completed') && booking.driver_name && (
                              <button
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowDetailsModal(true);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#2196F3',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                View Details
                              </button>
                            )}
                            {booking.status === 'confirmed' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'in_progress')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#4CAF50',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Start Service
                              </button>
                            )}
                            {booking.status === 'in_progress' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'completed')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#808080',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Complete
                              </button>
                            )}
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#f44336',
                                  border: 'none',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                Cancel
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setNotes(booking.notes || '');
                              }}
                              style={{
                                padding: '0.4rem 0.8rem',
                                background: 'var(--gold)',
                                border: 'none',
                                color: 'black',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold'
                              }}
                            >
                              Notes
                            </button>
                          </div>
                        </td>
                      </tr>
                      {selectedBooking?.id === booking.id && (
                        <tr style={{ background: 'rgba(205, 182, 136, 0.05)' }}>
                          <td colSpan="6" style={{ padding: '1rem' }}>
                            <div style={{ background: 'rgba(0, 0, 0, 0.02)', padding: '1rem', borderRadius: '4px', border: '1px solid rgba(205, 182, 136, 0.3)' }}>
                              <h4 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Notes & Details</h4>
                              <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add notes about this booking..."
                                style={{
                                  width: '100%',
                                  minHeight: '100px',
                                  padding: '0.75rem',
                                  background: 'white',
                                  border: '1px solid rgba(205, 182, 136, 0.3)',
                                  borderRadius: '4px',
                                  color: '#000',
                                  resize: 'vertical',
                                  marginBottom: '1rem'
                                }}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => updateNotes(booking.id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'var(--gold)',
                                    border: 'none',
                                    color: 'black',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Save Notes
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedBooking(null);
                                    setNotes('');
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid #ccc',
                                    color: '#666',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Manage Drivers</h3>
              <button
                onClick={() => setShowAddDriverModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  color: 'black',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                + Add Driver
              </button>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Phone</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>License</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <tr key={driver.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {driver.photo_url && (
                            <img src={driver.photo_url} alt={driver.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                          )}
                          <span style={{ fontWeight: 'bold' }}>{driver.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>{driver.phone}</td>
                      <td style={{ padding: '1rem' }}>{driver.email || '-'}</td>
                      <td style={{ padding: '1rem' }}>{driver.license_number || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Manage Vehicles</h3>
              <button
                onClick={() => setShowAddVehicleModal(true)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  color: 'black',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                + Add Vehicle
              </button>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Make & Model</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Color</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>License Plate</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Year</th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#666' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {vehicle.vehicle_image_url && (
                            <img
                              src={vehicle.vehicle_image_url}
                              alt={`${vehicle.make} ${vehicle.model}`}
                              style={{ width: '56px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                          )}
                          <span style={{ fontWeight: 'bold' }}>{vehicle.make} {vehicle.model}</span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>{vehicle.color}</td>
                      <td style={{ padding: '1rem' }}><span style={{ padding: '0.25rem 0.5rem', background: '#f0f0f0', borderRadius: '4px', fontFamily: 'monospace' }}>{vehicle.plate}</span></td>
                      <td style={{ padding: '1rem' }}>{vehicle.year || '-'}</td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => openEditVehicleModal(vehicle)}
                          style={{
                            padding: '0.5rem 0.9rem',
                            background: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Driver Modal */}
      {showAddDriverModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>Add New Driver</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Name *"
                value={newDriver.name}
                onChange={(e) => setNewDriver({...newDriver, name: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="tel"
                placeholder="Phone *"
                value={newDriver.phone}
                onChange={(e) => setNewDriver({...newDriver, phone: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="email"
                placeholder="Email"
                value={newDriver.email}
                onChange={(e) => setNewDriver({...newDriver, email: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="License Number"
                value={newDriver.license_number}
                onChange={(e) => setNewDriver({...newDriver, license_number: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="url"
                placeholder="Photo URL"
                value={newDriver.photo_url}
                onChange={(e) => setNewDriver({...newDriver, photo_url: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={addDriver}
                  disabled={!newDriver.name || !newDriver.phone}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: (!newDriver.name || !newDriver.phone) ? 0.5 : 1
                  }}
                >
                  Add Driver
                </button>
                <button
                  onClick={() => setShowAddDriverModal(false)}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddVehicleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>Add New Vehicle</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Make (e.g., BMW) *"
                value={newVehicle.make}
                onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Model (e.g., i7) *"
                value={newVehicle.model}
                onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Color *"
                value={newVehicle.color}
                onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="License Plate *"
                value={newVehicle.plate}
                onChange={(e) => setNewVehicle({...newVehicle, plate: e.target.value.toUpperCase()})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', textTransform: 'uppercase' }}
              />
              <input
                type="number"
                placeholder="Year"
                value={newVehicle.year}
                onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />

              <div style={{ border: '1px dashed #ccc', borderRadius: '8px', padding: '1rem', background: '#fafafa' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: 'bold' }}>
                  Vehicle Image *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleVehicleImageSelect}
                  style={{ width: '100%' }}
                />
                {vehicleImagePreview && (
                  <img
                    src={vehicleImagePreview}
                    alt="Vehicle preview"
                    style={{ marginTop: '0.75rem', width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={addVehicle}
                  disabled={!newVehicle.make || !newVehicle.model || !newVehicle.color || !newVehicle.plate || !vehicleImageFile || uploadingVehicleImage}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: (!newVehicle.make || !newVehicle.model || !newVehicle.color || !newVehicle.plate || !vehicleImageFile || uploadingVehicleImage) ? 0.5 : 1
                  }}
                >
                  {uploadingVehicleImage ? 'Uploading Image...' : 'Add Vehicle'}
                </button>
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    setVehicleImageFile(null);
                    setVehicleImagePreview('');
                  }}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vehicle Modal */}
      {showEditVehicleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '520px',
            width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>Edit Vehicle</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Make *"
                value={editVehicle.make}
                onChange={(e) => setEditVehicle({ ...editVehicle, make: e.target.value })}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Model *"
                value={editVehicle.model}
                onChange={(e) => setEditVehicle({ ...editVehicle, model: e.target.value })}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Color *"
                value={editVehicle.color}
                onChange={(e) => setEditVehicle({ ...editVehicle, color: e.target.value })}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="License Plate *"
                value={editVehicle.plate}
                onChange={(e) => setEditVehicle({ ...editVehicle, plate: e.target.value.toUpperCase() })}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', textTransform: 'uppercase' }}
              />
              <input
                type="number"
                placeholder="Year"
                value={editVehicle.year}
                onChange={(e) => setEditVehicle({ ...editVehicle, year: e.target.value })}
                style={{ padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />

              <div style={{ border: '1px dashed #ccc', borderRadius: '8px', padding: '1rem', background: '#fafafa' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: 'bold' }}>
                  Vehicle Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditVehicleImageSelect}
                  style={{ width: '100%' }}
                />
                {editVehicleImagePreview ? (
                  <img
                    src={editVehicleImagePreview}
                    alt="Vehicle preview"
                    style={{ marginTop: '0.75rem', width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                ) : (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', color: '#666', background: '#fff' }}>
                    No image uploaded yet. Add one now.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button
                  onClick={updateVehicle}
                  disabled={!editVehicle.make || !editVehicle.model || !editVehicle.color || !editVehicle.plate || uploadingEditVehicleImage}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: (!editVehicle.make || !editVehicle.model || !editVehicle.color || !editVehicle.plate || uploadingEditVehicleImage) ? 0.5 : 1
                  }}
                >
                  {uploadingEditVehicleImage ? 'Uploading Image...' : 'Save Changes'}
                </button>
                <button
                  onClick={closeEditVehicleModal}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal */}
      {showDriverModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Assign Driver & Vehicle</h3>
              <button
                onClick={() => setShowDriverModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Booking for:</strong> {selectedBooking.user_name}</p>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Date:</strong> {new Date(selectedBooking.service_date).toLocaleDateString()}</p>
              <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}><strong>Time:</strong> {selectedBooking.service_time} - {selectedBooking.end_time}</p>
            </div>

            {selectedBooking.requested_vehicle_id && (() => {
              const rv = vehicles.find(v => v.id === selectedBooking.requested_vehicle_id);
              if (!rv) return null;
              return (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '2px solid var(--gold)', borderRadius: '8px', background: 'rgba(205,182,136,0.1)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {rv.vehicle_image_url ? (
                    <img src={rv.vehicle_image_url} alt={`${rv.make} ${rv.model}`} style={{ width: '64px', height: '44px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: '64px', height: '44px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#888', fontWeight: 'bold', flexShrink: 0 }}>NO IMAGE</div>
                  )}
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#7d6a3f', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client Requested</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#000' }}>{rv.make} {rv.model}</div>
                    <div style={{ fontSize: '0.8rem', color: '#555' }}>{rv.color}{rv.year ? ` · ${rv.year}` : ''} · {rv.plate}</div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#000' }}>Select Driver *</label>
                <select
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  required
                >
                  <option value="">-- Choose a driver --</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} - {driver.phone}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#000' }}>Select Vehicle *</label>
                <select
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  required
                >
                  <option value="">-- Choose a vehicle --</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.color} {vehicle.make} {vehicle.model} ({vehicle.plate})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => assignDriver(selectedBooking.id)}
                  disabled={!selectedDriverId || !selectedVehicleId}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    opacity: (!selectedDriverId || !selectedVehicleId) ? 0.5 : 1
                  }}
                >
                  Assign & Confirm Booking
                </button>
                <button
                  onClick={() => setShowDriverModal(false)}
                  style={{
                    padding: '1rem',
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--gold)', margin: 0 }}>Booking Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Booking Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Service Information</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Date:</strong>{' '}
                    <span>{new Date(selectedBooking.service_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Time:</strong>{' '}
                    <span>{selectedBooking.service_time} - {selectedBooking.end_time}</span>
                  </div>
                  {selectedBooking.duration_hours && (
                    <div>
                      <strong style={{ color: '#666' }}>Duration:</strong>{' '}
                      <span>{selectedBooking.duration_hours} hour(s)</span>
                    </div>
                  )}
                  <div>
                    <strong style={{ color: '#666' }}>Status:</strong>{' '}
                    {getStatusBadge(selectedBooking.status)}
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Client Details</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Name:</strong>{' '}
                    <span>{selectedBooking.user_name}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Email:</strong>{' '}
                    <span>{selectedBooking.user_email}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Phone:</strong>{' '}
                    <span>{selectedBooking.user_phone}</span>
                  </div>
                </div>
              </div>

              {/* Route Info */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Route</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ color: '#666' }}>Pickup:</strong>{' '}
                    <div>{selectedBooking.pickup_address}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#666' }}>Destination:</strong>{' '}
                    <div>{selectedBooking.destination_address}</div>
                  </div>
                </div>
              </div>

              {/* Client Requests */}
              {(selectedBooking.requested_vehicle_id || selectedBooking.cpo_requested) && (
                <div style={{ padding: '1rem', background: 'rgba(205, 182, 136, 0.08)', border: '1px solid rgba(205,182,136,0.4)', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#000' }}>Client Requests</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {selectedBooking.requested_vehicle_id && vehicles.find(v => v.id === selectedBooking.requested_vehicle_id) && (() => {
                      const rv = vehicles.find(v => v.id === selectedBooking.requested_vehicle_id);
                      return (
                        <div>
                          <strong style={{ color: '#666' }}>Requested Vehicle:</strong>{' '}
                          <span>{rv.color} {rv.make} {rv.model} &middot; {rv.plate}{rv.year ? ` (${rv.year})` : ''}</span>
                        </div>
                      );
                    })()}
                    {selectedBooking.cpo_requested && (
                      <div>
                        <strong style={{ color: '#666' }}>CPO:</strong>{' '}
                        <span style={{ color: '#000', fontWeight: '500' }}>🛡 Close Protection Officer requested</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Driver & Vehicle Info */}
              {selectedBooking.driver_name && (
                <div style={{ padding: '1rem', background: 'rgba(205, 182, 136, 0.1)', border: '2px solid var(--gold)', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: 'var(--gold)' }}>Assigned Driver & Vehicle</h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        {selectedBooking.driver_photo_url && (
                          <img 
                            src={selectedBooking.driver_photo_url} 
                            alt={selectedBooking.driver_name}
                            style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--gold)' }}
                          />
                        )}
                        <div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{selectedBooking.driver_name}</div>
                          <div style={{ color: '#666' }}>{selectedBooking.driver_phone}</div>
                        </div>
                      </div>
                    </div>
                    {selectedBooking.vehicle_make && (
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(205, 182, 136, 0.3)' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                          {selectedBooking.vehicle_color} {selectedBooking.vehicle_make} {selectedBooking.vehicle_model}
                        </div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--gold)' }}>
                          {selectedBooking.vehicle_plate}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice & Receipt */}
              <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Transaction Documents</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#000' }}>Invoice</div>
                      {selectedBooking.invoice_url ? (
                        <a
                          href={selectedBooking.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2196F3', fontSize: '0.9rem', textDecoration: 'underline' }}
                        >
                          View uploaded invoice
                        </a>
                      ) : (
                        <div style={{ color: '#888', fontSize: '0.9rem' }}>No invoice uploaded yet</div>
                      )}
                    </div>
                    <label style={{ margin: 0 }}>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (file) {
                            saveTransactionDocument('invoice', file);
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingInvoice}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.6rem 1rem',
                          background: 'var(--gold)',
                          color: 'black',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          cursor: uploadingInvoice ? 'not-allowed' : 'pointer',
                          opacity: uploadingInvoice ? 0.6 : 1
                        }}
                      >
                        {uploadingInvoice ? 'Uploading...' : selectedBooking.invoice_url ? 'Replace Invoice' : 'Upload Invoice'}
                      </span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#000' }}>Receipt</div>
                      {selectedBooking.receipt_url ? (
                        <a
                          href={selectedBooking.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2196F3', fontSize: '0.9rem', textDecoration: 'underline' }}
                        >
                          View uploaded receipt
                        </a>
                      ) : (
                        <div style={{ color: '#888', fontSize: '0.9rem' }}>No receipt uploaded yet</div>
                      )}
                    </div>
                    <label style={{ margin: 0 }}>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (file) {
                            saveTransactionDocument('receipt', file);
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingReceipt}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.6rem 1rem',
                          background: '#2196F3',
                          color: 'white',
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          cursor: uploadingReceipt ? 'not-allowed' : 'pointer',
                          opacity: uploadingReceipt ? 0.6 : 1
                        }}
                      >
                        {uploadingReceipt ? 'Uploading...' : selectedBooking.receipt_url ? 'Replace Receipt' : 'Upload Receipt'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--gold)',
                  border: 'none',
                  color: 'black',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LondonVIPAdminDashboard;
